import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, inject, input, resource } from '@angular/core';
import { RegisterTabComponent } from '@core/decorators/register-tab-component.decorator';
import { TrainingUserListFacade } from '@features/academy/facades/training-user-list.facade';
import { TrainingUserListService } from '@features/academy/services/training-user-list.service';
import { TrainingUserListTableConfigService } from '@features/academy/services/training-user-list-table-config.service';
import { TrainingUserListFilterStore } from '@features/academy/stores/training-user-list-filter.store';
import { OrganizationTabKeys } from '@features/organization/constants/organization-tab-keys.constant';
import { ScrollToTopComponent } from '@features/work-item/kyc/components/scroll-to-top/scroll-to-top.component';
import { TranslocoDirective } from '@jsverse/transloco';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import type { ResponseTrainingUserDto } from '@kerberos-compliance/compliance-shared-ts/features/training/dtos/response.training-user.dto';
import type {
  Filtering,
  NoSearchResultsMessages,
  TableColumn,
  TableQueryParameters,
} from '@kerberos-compliance/compliance-shared-ui';
import { AppDatatableComponent, TranslationService } from '@kerberos-compliance/compliance-shared-ui';
import { TitleComponent } from '@shared/components/title/title.component';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';

@RegisterTabComponent(OrganizationTabKeys.AcademyUsers)
@Component({
  selector: 'aad-training-user-list',
  templateUrl: 'training-user-list.component.html',
  styleUrl: './training-user-list.component.less',
  providers: [
    TrainingUserListFacade,
    TrainingUserListService,
    TrainingUserListFilterStore,
    TrainingUserListTableConfigService,
  ],
  imports: [
    AppDatatableComponent,
    ScrollToTopComponent,
    NzColDirective,
    NzRowDirective,
    TranslocoDirective,
    TitleComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrainingUserListComponent implements OnInit {
  public readonly organizationId = input<UUID>();

  private readonly facade = inject(TrainingUserListFacade);

  private readonly translationService = inject(TranslationService);

  protected readonly tableColumns = this.facade.tableColumns;

  protected readonly tableData = this.facade.users;

  protected readonly pageIndex = this.facade.datatablePageIndex;

  protected readonly hasMore = this.facade.hasMore;

  protected readonly isTableLoading = this.facade.isLoading;

  protected readonly totalCount = this.facade.totalCount;

  protected readonly partialVsTotalCount = computed(
    () =>
      `${this.facade.partialCountItems()} ${this.translationService.getTranslation('general.pagination.of')} ${this.totalCount()}`,
  );

  protected readonly showNoResultsErrorMessage = this.facade.showNoResultsErrorMessage;

  protected readonly noResultsErrorMessages =
    this.translationService.translateObject<NoSearchResultsMessages>('general.noSearchResult');

  // noinspection JSUnusedGlobalSymbols - placeholder to keep the resource
  protected retrieveDataOnIdChange = resource({
    params: () => this.organizationId(),
    loader: () => {
      this.facade.setOrganizationId(this.organizationId());
      return this.facade.loadUsers();
    },
  });

  /**
   * Initializes the facade when the component lifecycle begins.
   *
   * @returns {void} This method does not return a value.
   */
  public ngOnInit(): void {
    this.facade.initialize();
  }

  /**
   * Handles changes in table query parameters by delegating to the facade.
   *
   * @param {TableQueryParameters} parameters - Pagination, sorting, and loading state.
   * @returns {void} This method does not return a value.
   */
  protected handleQueryParamsChange(parameters: TableQueryParameters): void {
    this.facade.loadDataFromServer(parameters);
  }

  /**
   * Exports the academy user list as an XLSX file.
   *
   * @param {Filtering} filterMode - The filtering mode to apply when exporting.
   * @returns {Promise<void>} Resolves when the export download has been initiated.
   */
  protected async exportAsXlsxFile(filterMode: Filtering): Promise<void> {
    await this.facade.exportAsXlsx(filterMode);
  }

  /**
   * Persists the academy user table configuration.
   *
   * @param {TableColumn<ResponseTrainingUserDto>[]} columns - The current column configuration.
   * @returns {Promise<void>} Resolves once the configuration has been persisted.
   */
  protected async saveTableConfiguration(columns: TableColumn<ResponseTrainingUserDto>[]): Promise<void> {
    await this.facade.saveTableConfiguration(columns);
  }
}
